import { TChapter, TCourse, TEpic } from "./course";
import { TLesson } from "./lesson";

// Fonction pour trouver une leçon dans le cours
export function findLessonInCourse(course: TCourse, lessonId: number): TLesson | undefined {
    for (const chapter of course.chapters) {
        for (const epic of chapter.epics) {
            for (const lesson of epic.lessons) {
                if (lesson.id === lessonId) {
                    return lesson;
                }
            }
        }
    }
    return undefined;
}

export function findEpicInCourse(
    course: TCourse | null | undefined,
    epicId: number | string | null | undefined
): TEpic | null {
    if (!course || epicId == null) return null;

    const chapters = Array.isArray(course.chapters) ? course.chapters : [];

    for (const chapter of chapters) {
        const epics = Array.isArray(chapter?.epics) ? chapter!.epics : [];
        for (const epic of epics) {
            if (epic && epic.id === epicId) {
                return epic;
            }
        }
    }

    return null;
}

export function getAllLessonsFromCourse(course: TCourse | null): TLesson[] {
    if (!course) {
        return []
    }
    try {
        return course.chapters
            .filter((chapter: TChapter | null) => chapter !== null)
            .flatMap((chapter: TChapter) =>
                (chapter.epics ?? [])
                    .filter((epic) => epic !== null)
                    .flatMap((epic) => epic.lessons ?? [])
            );
    } catch {
        return []
    }

}
export type TCoursePosition = {
    chapter: TChapter;
    epic: TEpic;
    lesson: TLesson;
};

const byOrder = <T extends { [key: string]: any }>(items: T[], field: string): T[] =>
    [...items].sort((a, b) => (a?.[field] ?? 0) - (b?.[field] ?? 0));

/**
 * Parcourt le cours dans l'ordre pedagogique reel (chapitre, puis epopee, puis
 * lecon) en s'appuyant sur les champs *_order plutot que sur l'ordre de
 * serialisation de l'API, et renvoie chaque lecon avec son chapitre et son
 * epopee. C'est la base de toute navigation "lecon suivante" : sans le chapitre
 * et l'epopee on ne sait pas quelle section ouvrir quand on franchit une
 * frontiere d'epopee ou de chapitre.
 */
export function getCourseOutline(course: TCourse | null | undefined): TCoursePosition[] {
    if (!course) return [];

    const chapters = Array.isArray(course.chapters) ? course.chapters : [];
    const outline: TCoursePosition[] = [];

    for (const chapter of byOrder(chapters.filter((ch) => !!ch), 'chapter_order')) {
        const epics = Array.isArray(chapter.epics) ? chapter.epics : [];
        for (const epic of byOrder(epics.filter((ep) => !!ep), 'epic_order')) {
            const lessons = Array.isArray(epic.lessons) ? epic.lessons : [];
            for (const lesson of byOrder(lessons.filter((l) => !!l), 'lesson_order')) {
                outline.push({ chapter, epic, lesson });
            }
        }
    }

    return outline;
}

/**
 * Lecon qui suit immediatement `lessonId` dans le cours, toutes epopees et tous
 * chapitres confondus. Renvoie null seulement si la lecon est la derniere du
 * cours (ou introuvable).
 */
export function findNextCoursePosition(
    course: TCourse | null | undefined,
    lessonId: number | null | undefined
): TCoursePosition | null {
    if (!lessonId) return null;

    const outline = getCourseOutline(course);
    const currentIndex = outline.findIndex((position) => position.lesson.id === lessonId);
    if (currentIndex < 0) return null;

    return outline[currentIndex + 1] ?? null;
}
